import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import {
  DEFAULT_TOURNAMENT_PAGE_SIZE,
  TOURNAMENT_LIST_PARAM as PARAM,
  parseTournamentListParams,
} from '@/utils/tournamentListParams';
import type { TournamentListParamName } from '@/utils/tournamentListParams';
import { SEARCH_DEBOUNCE_MS } from '@/constants';
import type { TournamentRole, TournamentStatus } from '@/constants';

/** Inactive tab slides read the defaults. */
const NO_PARAMS = new URLSearchParams();

export interface TournamentListFilters {
  /** Live text of the search input (not debounced). */
  query: string;
  setQuery: (value: string) => void;
  /** Debounced, trimmed search sent to the API. */
  search: string;
  status: TournamentStatus | null;
  setStatus: (value: TournamentStatus | null) => void;
  /** Four-digit season year, or null for all seasons. */
  season: string | null;
  setSeason: (value: string | null) => void;
  role: TournamentRole | null;
  setRole: (value: TournamentRole | null) => void;
  /** 0-based page index (the URL keeps it 1-based). */
  page: number;
  setPage: (value: number) => void;
  pageSize: number;
  setPageSize: (value: number) => void;
  /** Clears search, status, season and role. */
  resetFilters: () => void;
}

/**
 * Tournament list filter and pagination state stored in the URL search
 * params (`q`, `status`, `season`, `role`, `page`, `size`), so it survives opening a tournament
 * and navigating back. Filter changes return to the first page. Search input is local and written to the URL debounced.
 *
 * `isActive` — on mobile every tab slide stays mounted while they share one
 * URL; inactive tabs ignore the URL and read the defaults instead.
 */
export function useTournamentListFilters(isActive: boolean): TournamentListFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    q: urlQuery, status, season, role, page, pageSize,
  } = parseTournamentListParams(isActive ? searchParams : NO_PARAMS);

  const [query, setQuery] = useState(urlQuery);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  // Latest input text, also moved synchronously by the URL sync below, so a
  // debounced value that is already outdated never gets written.
  const latestQueryRef = useRef(query);
  latestQueryRef.current = query;

  /** Applies `changes` (null / default → removed) to the current URL params. */
  const update = useCallback((changes: Partial<Record<TournamentListParamName, string | null>>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Last `q` this hook wrote. A different URL value came from outside (nav
  // link, back, the tab slide going inactive) and replaces the input text, so
  // the field never shows a search the list is not filtered by.
  const writtenQueryRef = useRef(urlQuery);
  useEffect(() => {
    if (urlQuery === writtenQueryRef.current) return;
    writtenQueryRef.current = urlQuery;
    latestQueryRef.current = urlQuery;
    setQuery(urlQuery);
  }, [urlQuery]);

  // Debounced search → URL. A debounced value that no longer matches the input
  // (the URL changed from outside, e.g. the header search, after it was typed)
  // is stale and skipped.
  useEffect(() => {
    if (!isActive || debouncedQuery !== latestQueryRef.current) return;
    const trimmed = debouncedQuery.trim();
    if (trimmed === urlQuery) return;
    writtenQueryRef.current = trimmed;
    update({ [PARAM.QUERY]: trimmed || null, [PARAM.PAGE]: null });
    // Only a new debounced value should write; urlQuery changes are read-only here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, isActive, update]);

  const setStatus = useCallback((value: TournamentStatus | null) => {
    update({ [PARAM.STATUS]: value, [PARAM.PAGE]: null });
  }, [update]);

  const setSeason = useCallback((value: string | null) => {
    update({ [PARAM.SEASON]: value, [PARAM.PAGE]: null });
  }, [update]);

  const setRole = useCallback((value: TournamentRole | null) => {
    update({ [PARAM.ROLE]: value, [PARAM.PAGE]: null });
  }, [update]);

  const setPage = useCallback((value: number) => {
    update({ [PARAM.PAGE]: value > 0 ? String(value + 1) : null });
  }, [update]);

  const setPageSize = useCallback((value: number) => {
    update({ [PARAM.SIZE]: value === DEFAULT_TOURNAMENT_PAGE_SIZE ? null : String(value), [PARAM.PAGE]: null });
  }, [update]);

  const resetFilters = useCallback(() => {
    writtenQueryRef.current = '';
    setQuery('');
    update({
      [PARAM.QUERY]: null,
      [PARAM.STATUS]: null,
      [PARAM.SEASON]: null,
      [PARAM.ROLE]: null,
      [PARAM.PAGE]: null,
    });
  }, [update]);

  return useMemo(() => ({
    query,
    setQuery,
    search: urlQuery,
    status,
    setStatus,
    season,
    setSeason,
    role,
    setRole,
    page,
    setPage,
    pageSize,
    setPageSize,
    resetFilters,
  }), [
    query, urlQuery, status, setStatus, season, setSeason, role, setRole,
    page, setPage, pageSize, setPageSize, resetFilters,
  ]);
}
